import { Apple, Play, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { SectionHeading } from "@/components/PageIntro";
import PhoneMockup from "@/components/PhoneMockup";
import { AppStoreBadge, GooglePlayBadge } from "@/components/StoreBadges";
import StoreQr from "@/components/StoreQr";
import { trackAndroid, trackIos } from "@/lib/stats";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "@/lib/links";

const STORES = [
  {
    Icon: Apple,
    kind: "ios",
    platform: "iOS",
    status: "Offiziell verfügbar",
    statusColor: "#22C55E",
    title: "Im App Store herunterladen",
    note: "FestivalPlan ist ab sofort für iPhone und iPad im Apple App Store erhältlich – kostenlos.",
    href: APP_STORE_URL,
    cta: "Im App Store herunterladen",
    track: trackIos,
  },
  {
    Icon: Play,
    kind: "android",
    platform: "Android",
    status: "Offiziell verfügbar",
    statusColor: "#22C55E",
    title: "Bei Google Play herunterladen",
    note: "FestivalPlan ist ab sofort für Android im Google Play Store erhältlich – kostenlos.",
    href: GOOGLE_PLAY_URL,
    cta: "Bei Google Play herunterladen",
    track: trackAndroid,
  },
];

export default function Download({ compact = false }) {
  return (
    <section id="download" data-testid="download-section" className="border-b border-white/10">
      <div className="mx-auto max-w-6xl px-5 py-20 lg:px-8 lg:py-24">
        {!compact && (
          <Reveal>
            <SectionHeading
              eyebrow="Download"
              title="FestivalPlan ist da!"
              lead="Jetzt offiziell für Android und iOS verfügbar – im Google Play Store und im Apple App Store. Kostenlos herunterladen und loslegen."
            />
          </Reveal>
        )}
        {compact && (
          <Reveal>
            <div
              data-testid="download-preview"
              className="mb-12 flex items-end justify-center gap-4 sm:gap-6"
            >
              <div className="w-[118px] opacity-90 sm:w-[150px]">
                <PhoneMockup
                  src="/generated/app_uebersicht.png"
                  alt="FestivalPlan Übersicht"
                  tilt={-6}
                  sheen={false}
                  island={false}
                  testId="download-phone-uebersicht"
                />
              </div>
              <div className="w-[118px] opacity-90 sm:w-[150px]">
                <PhoneMockup
                  src="/generated/app_mitbringliste.png"
                  alt="FestivalPlan Mitbringliste"
                  glow="cyan"
                  tilt={6}
                  sheen={false}
                  island={false}
                  testId="download-phone-mitbringliste"
                />
              </div>
            </div>
          </Reveal>
        )}
        <Reveal delay={0.04}>
          <div
            data-testid="store-badges"
            className="flex flex-wrap items-center gap-4 pb-2"
          >
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackIos}
              data-testid="badge-appstore"
              aria-label="Im App Store herunterladen"
              className="inline-flex transition-transform duration-200 hover:-translate-y-0.5"
            >
              <AppStoreBadge className="h-[54px] w-auto" />
            </a>
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackAndroid}
              data-testid="badge-googleplay"
              aria-label="Bei Google Play holen"
              className="inline-flex transition-transform duration-200 hover:-translate-y-0.5"
            >
              <GooglePlayBadge className="h-[54px] w-auto" />
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <ul className={`${compact ? "" : "mt-10 border-t border-white/10"} divide-y divide-white/10`}>
            {STORES.map((s) => (
              <li
                key={s.kind}
                data-testid={`platform-${s.kind}`}
                className="grid grid-cols-1 gap-5 py-8 md:grid-cols-[1fr_auto] md:items-center lg:grid-cols-[1fr_auto_auto] lg:gap-8"
              >
                <div className="flex gap-4">
                  <span className="mt-0.5 grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-white/10 bg-white/[0.03]">
                    <s.Icon className="h-5 w-5" style={{ color: s.statusColor }} />
                  </span>
                  <div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <h3 className="font-display text-xl font-semibold text-white">{s.platform}</h3>
                      <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest" style={{ color: s.statusColor }}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.statusColor }} />
                        {s.status}
                      </span>
                    </div>
                    <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-white/55">{s.note}</p>
                  </div>
                </div>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={s.track}
                  data-testid={`store-${s.title.toLowerCase().replace(/\s/g, "-")}`}
                  className="btn-primary group inline-flex items-center justify-center gap-2 whitespace-nowrap px-6 py-3 text-sm font-medium"
                >
                  <s.Icon className="h-4 w-4" />
                  {s.cta}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </a>
                <StoreQr
                  value={s.href}
                  label="Mit dem Handy scannen"
                  size={88}
                  className="hidden lg:flex"
                  testId={`qr-${s.kind}`}
                />
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}
